import { css } from '@emotion/react';
import { observer } from 'mobx-react-lite';
import { ReactElement, useCallback, useEffect, useRef } from 'react';

import { useSDK } from '../../../../../app/App';
import { styled } from '../../../../../styled';
import type { Theme } from '../../../../../theme/interface/detailed';
import { useTheme } from '../../../../../theme/useTheme';

type Interface = { className?: string; onClick: () => void };

const styles = css`
	cursor: pointer;
	height: 21px;
	&:focus {
		outline: none;
	}
	svg {
		background-color: transparent;
	}
`;

const Component = ({ className = '', onClick }: Interface): ReactElement => {
	const { attachmentsStore } = useSDK();
	const theme = useTheme();

	const iconsRef = useRef<HTMLDivElement>(null);

	const onKeyPress = useCallback(
		(e: React.KeyboardEvent<HTMLDivElement>): void => {
			if (e.key === 'Enter') {
				onClick();
			}
		},
		[onClick]
	);
	useEffect(() => {
		if (attachmentsStore.unsentAttachments.size > 0) iconsRef.current?.blur();
	}, [attachmentsStore.unsentAttachments.size]);

	return (
		<div ref={iconsRef} className={className} onKeyPress={onKeyPress} tabIndex={0} onClick={onClick}>
			<svg
				width={theme.chat.icons.messageAttachmentIcon.size}
				viewBox="0 0 24 24"
				fill="none"
				xmlns="http://www.w3.org/2000/svg"
			>
				<path
					d="M8 23c-1.79 0-3.59-.68-4.95-2.05a7.007 7.007 0 010-9.9l8.57-8.57a5.003 5.003 0 018.54 3.54c0 1.34-.52 2.59-1.47 3.54l-8.58 8.57a3 3 0 01-4.24 0c-.56-.58-.88-1.33-.88-2.13 0-.8.31-1.56.88-2.12l7.87-7.87a.996.996 0 011.41 0c.39.39.39 1.02 0 1.42l-7.87 7.86c-.39.39-.39 1.02 0 1.42.39.39 1.03.39 1.42 0l8.58-8.57c.57-.57.88-1.32.88-2.12s-.31-1.56-.88-2.12a3.012 3.012 0 00-4.25 0l-8.57 8.57c-1.95 1.95-1.95 5.12 0 7.08a5.016 5.016 0 007.08 0l8.57-8.57a.996.996 0 111.41 1.41l-8.57 8.57A7.013 7.013 0 018 23z"
					fill={theme.chat.icons.messageAttachmentIcon.fill}
				/>
			</svg>
		</div>
	);
};

const MessageAttachmentIcon = styled(observer(Component))(
	({
		theme: {
			chat: {
				icons: {
					messageAttachmentIcon: { colorOnFocus, ...other },
				},
			},
		},
	}: {
		theme: Theme;
	}) => ({
		'&:focus': {
			path: { fill: colorOnFocus },
		},
		'&:active': {
			path: { fill: colorOnFocus },
		},
		'& > svg': {
			...other,
		},
	}),
	styles
);

export { MessageAttachmentIcon };
